import { useLayoutEffect, useSyncExternalStore } from 'react';

/**
 * Focus mode: while a screen shows a sticky action bar (or the new-member profile form), the
 * phone tab bar and the footer step aside so only one bar ever sits at the bottom of the screen.
 * Components opt in with `useFocusMode()`; the shell reads `useIsFocusMode()`.
 */
let holders = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Turn focus mode on while the calling component is mounted (and `active` is true). */
export function useFocusMode(active = true): void {
  useLayoutEffect(() => {
    if (!active) return undefined;
    holders += 1;
    emit();
    return () => {
      holders = Math.max(0, holders - 1);
      emit();
    };
  }, [active]);
}

/** True while any mounted component holds focus mode. */
export function useIsFocusMode(): boolean {
  return useSyncExternalStore(subscribe, () => holders > 0, () => false);
}
